// src/client/renderer/vfx/env/birds-pool.ts
//
// Plan 18, Task 4.2 — Birds ambient pool.
//
// Behaviour contract:
//   - ALWAYS-ON ambient pool. mode: 'ambient'.
//   - Single-instance idempotent re-acquire: second acquire() returns the SAME
//     THREE.Group containing all birds.
//   - Spawns 3-5 birds (BIRD_COUNT = 4 deterministic) at scene-build time.
//     Each bird is a thin THREE.Mesh (chevron BufferGeometry — 3 vertices
//     forming a chevron) with a single-sided MeshBasicMaterial (0xe8e8e8).
//   - Animation: each bird follows a closed CubicBezierCurve3 along the sky
//     horizon (radius ~80, period 25-40s per bird, deterministic via index
//     seed). The bird's lookAt target is its forward tangent so the chevron
//     points along its path.
//   - Curve choice: THREE.CubicBezierCurve3 — chosen for native Three.js
//     support of getPointAt(u) and getTangentAt(u), giving smooth closed
//     parametric curves without hand-rolling math.
//   - isFinished always returns false (birds are perpetual).
//   - dispose() disposes each bird's geometry + material once AND removes the
//     group from scene.
//
// Chevron geometry: 3 vertices forming a flat chevron pointing -Z (forward):
//   v0 = (-0.5, 0,  0.3)   (left wing tip)
//   v1 = ( 0.5, 0,  0.3)   (right wing tip)
//   v2 = ( 0,   0, -0.3)   (beak/nose)
// Winding: [0, 1, 2] → CCW from above (+Y normal) → FrontSide visible from
// above-down cameras (board-follow + zoom-out at y=8 and y=30 respectively).
// Spec requires single-sided basic material; back-face from below is invisible.
//
// Bezier control points (per-bird, index-derived):
//   phase = (i / BIRD_COUNT) * 2π
//   cp0 = cos(phase   ) * R, 25, sin(phase   ) * R
//   cp1 = cos(phase+0.5) * R, 30, sin(phase+0.5) * R
//   cp2 = cos(phase+1.0) * R, 28, sin(phase+1.0) * R
//   cp3 = cos(phase+1.5) * R, 25, sin(phase+1.5) * R
// Period = 25 + (i / BIRD_COUNT) * 15 s (range 25–40s across birds).
// This is deterministic via index, requires NO RNG.

import * as THREE from 'three';
import type {
  EnvCeremonyPool,
  EnvCeremonyPoolDeps,
  EnvCeremonySpawnArgs,
} from './env-ceremony-types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Number of birds to spawn. Fixed deterministic count. */
const BIRD_COUNT = 4;

/** Sky horizon radius for bezier curve control points. */
const BIRD_RADIUS = 80;

/** Minimum flight period (seconds). */
const PERIOD_MIN = 25.0;

/** Maximum flight period (seconds). */
const PERIOD_MAX = 40.0;

/** Bird material colour: white-grey. */
const BIRD_COLOUR = 0xe8e8e8;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers: chevron geometry
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a single-triangle chevron geometry pointing in the -Z direction.
 * Vertices: left wing tip, right wing tip, beak (nose).
 * Uses Float32BufferAttribute for position data.
 */
function makeChevronGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();

  // Flat chevron in XZ plane pointing -Z
  // v0 = left wing tip, v1 = right wing tip, v2 = beak
  const positions = new Float32Array([
    -0.5,
    0,
    0.3, // v0
    0.5,
    0,
    0.3, // v1
    0,
    0,
    -0.3, // v2
  ]);

  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex([0, 1, 2]); // CCW winding from above → +Y normal → FrontSide visible from above-camera (zoom-out at y=30)
  geometry.computeVertexNormals();

  return geometry;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers: per-bird bezier curve (deterministic by index)
// ─────────────────────────────────────────────────────────────────────────────

function makeBirdCurve(index: number): THREE.CubicBezierCurve3 {
  const phase = (index / BIRD_COUNT) * Math.PI * 2;
  const R = BIRD_RADIUS;

  const cp = (offset: number, yBase: number): THREE.Vector3 =>
    new THREE.Vector3(Math.cos(phase + offset) * R, yBase, Math.sin(phase + offset) * R);

  return new THREE.CubicBezierCurve3(cp(0.0, 25), cp(0.5, 30), cp(1.0, 28), cp(1.5, 25));
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-bird state
// ─────────────────────────────────────────────────────────────────────────────

interface BirdState {
  mesh: THREE.Mesh;
  curve: THREE.CubicBezierCurve3;
  /** Elapsed time (seconds) — drives position along curve. */
  elapsed: number;
  /** Period of one complete circuit (seconds). */
  period: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createBirdsPool({ scene }: EnvCeremonyPoolDeps): EnvCeremonyPool {
  /** The single group — null until first acquire(). */
  let sharedGroup: THREE.Group | null = null;

  /** Per-bird animation state. */
  const birdStates: BirdState[] = [];

  let disposed = false;

  // Reusable scratch vectors to avoid allocations in update()
  const _pos = new THREE.Vector3();
  const _tan = new THREE.Vector3();

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Idempotent re-acquire: return existing group on subsequent calls.
    if (sharedGroup !== null) return sharedGroup;

    const group = new THREE.Group();

    for (let i = 0; i < BIRD_COUNT; i++) {
      const geometry = makeChevronGeometry();
      const material = new THREE.MeshBasicMaterial({
        color: BIRD_COLOUR,
        side: THREE.FrontSide,
      });

      const mesh = new THREE.Mesh(geometry, material);

      // Place bird at its initial position on the curve
      const curve = makeBirdCurve(i);
      const period = PERIOD_MIN + (i / BIRD_COUNT) * (PERIOD_MAX - PERIOD_MIN);

      // Set initial position at t=0
      curve.getPointAt(0, _pos);
      mesh.position.copy(_pos);

      // Orient beak along forward tangent at t=0
      curve.getTangentAt(0, _tan);
      const lookTarget = _pos.clone().add(_tan);
      mesh.lookAt(lookTarget);

      group.add(mesh);

      birdStates.push({ mesh, curve, elapsed: 0, period });
    }

    scene.add(group);
    sharedGroup = group;
    return group;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed || sharedGroup === null) return new Set();

    for (const state of birdStates) {
      state.elapsed += deltaSeconds;

      // Normalized position along curve [0, 1], wrapping via modulo
      const u = (state.elapsed % state.period) / state.period;

      // Update position
      state.curve.getPointAt(u, _pos);
      state.mesh.position.copy(_pos);

      // Orient along tangent
      state.curve.getTangentAt(u, _tan);
      const lookTarget = _pos.clone().add(_tan);
      state.mesh.lookAt(lookTarget);
    }

    return new Set([sharedGroup]);
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(_emitter: THREE.Object3D): boolean {
    // Birds are perpetual — never finished
    return false;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (sharedGroup === null || emitter !== sharedGroup) return;

    scene.remove(sharedGroup);
    for (const state of birdStates) {
      state.mesh.geometry.dispose();
      (state.mesh.material as THREE.MeshBasicMaterial).dispose();
    }
    birdStates.length = 0;
    sharedGroup = null;
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    if (sharedGroup !== null) {
      scene.remove(sharedGroup);
      for (const state of birdStates) {
        state.mesh.geometry.dispose();
        (state.mesh.material as THREE.MeshBasicMaterial).dispose();
      }
      birdStates.length = 0;
      sharedGroup = null;
    }
  }

  return { mode: 'ambient', acquire, update, isFinished, release, dispose };
}
